/**
 * Comprehensive Patient Management Dashboard
 * Provides access to all patient management features
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Tabs,
  Tab,
  TabPanel
} from '@mui/material';
import {
  Person as ProfileIcon,
  History as HistoryIcon,
  CloudUpload as DocumentIcon,
  Assignment as IntakeIcon,
  Chat as MessageIcon,
  Star as FeedbackIcon,
  Receipt as BillingIcon,
  Security as PrivacyIcon,
  CheckCircle as ConsentIcon,
  Settings as SettingsIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Search as SearchIcon,
  Notifications as NotificationIcon,
  Language as LanguageIcon,
  Accessibility as AccessibilityIcon,
  MedicalServices as MedicalIcon,
  LocalHospital as HospitalIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  FileUpload as UploadIcon,
  FileDownload as DownloadFileIcon,
  Preview as PreviewIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// API Services
import {
  patientProfileApi,
  medicalHistoryApi,
  documentApi,
  consentApi,
  privacyApi,
  communicationApi,
  feedbackApi,
  billingApi
} from '../../services/api/patientManagement';

const PatientManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Data states
  const [profileData, setProfileData] = useState(null);
  const [medicalHistory, setMedicalHistory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [consents, setConsents] = useState([]);
  const [privacySettings, setPrivacySettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [invoices, setInvoices] = useState([]);
  
  // Dialog states
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Form states
  const [consentForm, setConsentForm] = useState({
    consent_type: '',
    consent_text: '',
    expires_at: null
  });
  const [privacyForm, setPrivacyForm] = useState({
    data_sharing_enabled: false,
    profile_visibility: 'private',
    search_visibility: false,
    data_retention_years: 7,
    auto_delete_enabled: false,
    anonymize_data: false
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all patient data in parallel
      const [
        profileResponse,
        medicalHistoryResponse,
        documentsResponse,
        consentsResponse,
        privacyResponse,
        messagesResponse,
        feedbackResponse,
        invoicesResponse
      ] = await Promise.allSettled([
        patientProfileApi.getProfile(),
        medicalHistoryApi.getMedicalHistory(),
        documentApi.getDocuments(),
        consentApi.getConsents(),
        privacyApi.getPrivacySettings(),
        communicationApi.getMessages(),
        feedbackApi.getFeedback(),
        billingApi.getInvoices()
      ]);

      // Set data from successful responses
      if (profileResponse.status === 'fulfilled') {
        setProfileData(profileResponse.value);
      }
      if (medicalHistoryResponse.status === 'fulfilled') {
        setMedicalHistory(medicalHistoryResponse.value);
      }
      if (documentsResponse.status === 'fulfilled') {
        setDocuments(documentsResponse.value);
      }
      if (consentsResponse.status === 'fulfilled') {
        setConsents(consentsResponse.value);
      }
      if (privacyResponse.status === 'fulfilled') {
        setPrivacySettings(privacyResponse.value);
        setPrivacyForm(privacyResponse.value);
      }
      if (messagesResponse.status === 'fulfilled') {
        setMessages(messagesResponse.value);
      }
      if (feedbackResponse.status === 'fulfilled') {
        setFeedback(feedbackResponse.value);
      }
      if (invoicesResponse.status === 'fulfilled') {
        setInvoices(invoicesResponse.value);
      }

    } catch (err) {
      console.error('Error loading patient data:', err);
      setError('Failed to load patient data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConsentGrant = async () => {
    try {
      setLoading(true);
      await consentApi.grantConsent(consentForm);
      toast.success('Consent granted successfully!');
      setShowConsentDialog(false);
      setConsentForm({ consent_type: '', consent_text: '', expires_at: null });
      await loadAllData();
    } catch (err) {
      console.error('Error granting consent:', err);
      toast.error('Failed to grant consent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConsentWithdraw = async (consentId) => {
    try {
      setLoading(true);
      await consentApi.withdrawConsent(consentId);
      toast.success('Consent withdrawn successfully!');
      await loadAllData();
    } catch (err) {
      console.error('Error withdrawing consent:', err);
      toast.error('Failed to withdraw consent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrivacyUpdate = async () => {
    try {
      setLoading(true);
      await privacyApi.updatePrivacySettings(privacyForm);
      toast.success('Privacy settings updated successfully!');
      setShowPrivacyDialog(false);
      await loadAllData();
    } catch (err) {
      console.error('Error updating privacy settings:', err);
      toast.error('Failed to update privacy settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDataExport = async () => {
    try {
      setLoading(true);
      const exportData = await privacyApi.exportData();
      
      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `patient-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully!');
      setShowExportDialog(false);
    } catch (err) {
      console.error('Error exporting data:', err);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDataDeletion = async () => {
    try {
      setLoading(true);
      await privacyApi.deleteData();
      toast.success('Data deletion request submitted successfully!');
      setShowDeleteDialog(false);
      await loadAllData();
    } catch (err) {
      console.error('Error deleting data:', err);
      toast.error('Failed to delete data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCompletionPercentage = () => {
    const sections = [
      profileData ? 'profile' : null,
      medicalHistory.length > 0 ? 'history' : null,
      documents.length > 0 ? 'documents' : null,
      consents.length > 0 ? 'consents' : null,
      privacySettings ? 'privacy' : null
    ].filter(Boolean);
    
    return Math.round((sections.length / 5) * 100);
  };

  const StatCard = ({ title, value, icon, color, onClick, loading }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease',
        '&:hover': onClick ? { transform: 'translateY(-4px)' } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              {loading ? <CircularProgress size={20} /> : value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const FeatureCard = ({ title, description, icon, color, onClick, status, count }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
          {count !== undefined && (
            <Badge badgeContent={count} color="primary">
              <Chip 
                label={status} 
                size="small" 
                color={status === 'Active' ? 'success' : 'default'}
              />
            </Badge>
          )}
        </Box>
      </CardContent>
      <CardActions>
        <Button size="small" onClick={onClick}>
          Manage
        </Button>
      </CardActions>
    </Card>
  );

  if (loading && !profileData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Patient Management Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Completion Status */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Profile Completion</Typography>
          <Chip 
            label={`${getCompletionPercentage()}% Complete`} 
            color={getCompletionPercentage() === 100 ? 'success' : 'primary'}
            variant="outlined"
          />
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={getCompletionPercentage()} 
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Paper>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Medical Records"
            value={medicalHistory.length}
            icon={<HistoryIcon />}
            color="primary.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Documents"
            value={documents.length}
            icon={<DocumentIcon />}
            color="secondary.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Consents"
            value={consents.filter(c => c.status === 'granted').length}
            icon={<ConsentIcon />}
            color="success.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Messages"
            value={messages.length}
            icon={<MessageIcon />}
            color="info.main"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Feature Management */}
      <Grid container spacing={3}>
        {/* Profile Management */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Profile Management"
            description="Manage your personal information and contact details"
            icon={<ProfileIcon />}
            color="primary.main"
            onClick={() => navigate('/patient-dashboard/profile')}
            status={profileData ? 'Complete' : 'Incomplete'}
          />
        </Grid>

        {/* Medical History */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Medical History"
            description="Track and manage your medical history and conditions"
            icon={<HistoryIcon />}
            color="secondary.main"
            onClick={() => navigate('/patient-dashboard/medical-history')}
            status={medicalHistory.length > 0 ? 'Active' : 'Empty'}
            count={medicalHistory.length}
          />
        </Grid>

        {/* Document Management */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Document Management"
            description="Upload, organize, and manage your medical documents"
            icon={<DocumentIcon />}
            color="info.main"
            onClick={() => navigate('/patient-dashboard/documents')}
            status={documents.length > 0 ? 'Active' : 'Empty'}
            count={documents.length}
          />
        </Grid>

        {/* Intake Forms */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Intake Forms"
            description="Complete detailed health assessment forms"
            icon={<IntakeIcon />}
            color="warning.main"
            onClick={() => navigate('/patient-dashboard/intake-forms')}
            status="Available"
          />
        </Grid>

        {/* Communication */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Communication"
            description="Message your healthcare providers securely"
            icon={<MessageIcon />}
            color="success.main"
            onClick={() => navigate('/patient-dashboard/messages')}
            status={messages.length > 0 ? 'Active' : 'Empty'}
            count={messages.length}
          />
        </Grid>

        {/* Feedback System */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Feedback System"
            description="Rate and provide feedback on your healthcare experience"
            icon={<FeedbackIcon />}
            color="error.main"
            onClick={() => navigate('/patient-dashboard/feedback')}
            status={feedback.length > 0 ? 'Active' : 'Empty'}
            count={feedback.length}
          />
        </Grid>

        {/* Billing Integration */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Billing & Invoices"
            description="View and manage your medical bills and payments"
            icon={<BillingIcon />}
            color="primary.main"
            onClick={() => navigate('/patient-dashboard/billing')}
            status={invoices.length > 0 ? 'Active' : 'Empty'}
            count={invoices.length}
          />
        </Grid>

        {/* Consent Management */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Consent Management"
            description="Manage your data processing and treatment consents"
            icon={<ConsentIcon />}
            color="secondary.main"
            onClick={() => setShowConsentDialog(true)}
            status={consents.length > 0 ? 'Active' : 'Empty'}
            count={consents.length}
          />
        </Grid>

        {/* Privacy Controls */}
        <Grid item xs={12} md={6}>
          <FeatureCard
            title="Privacy Controls"
            description="Control your data sharing and privacy settings"
            icon={<PrivacyIcon />}
            color="info.main"
            onClick={() => setShowPrivacyDialog(true)}
            status={privacySettings ? 'Configured' : 'Default'}
          />
        </Grid>
      </Grid>

      {/* Consent Management Dialog */}
      <Dialog open={showConsentDialog} onClose={() => setShowConsentDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Consent Management</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Current Consents
              </Typography>
              <List>
                {consents.map((consent) => (
                  <ListItem key={consent.id}>
                    <ListItemIcon>
                      <ConsentIcon color={consent.status === 'granted' ? 'success' : 'error'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={consent.consent_type}
                      secondary={`Status: ${consent.status} - ${consent.granted_at ? new Date(consent.granted_at).toLocaleDateString() : 'Not granted'}`}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        color={consent.status === 'granted' ? 'error' : 'primary'}
                        onClick={() => handleConsentWithdraw(consent.id)}
                      >
                        {consent.status === 'granted' ? 'Withdraw' : 'Grant'}
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Grant New Consent
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Consent Type</InputLabel>
                    <Select
                      value={consentForm.consent_type}
                      onChange={(e) => setConsentForm({ ...consentForm, consent_type: e.target.value })}
                      label="Consent Type"
                    >
                      <MenuItem value="data_processing">Data Processing</MenuItem>
                      <MenuItem value="treatment">Treatment</MenuItem>
                      <MenuItem value="communication">Communication</MenuItem>
                      <MenuItem value="research">Research</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Consent Text"
                    value={consentForm.consent_text}
                    onChange={(e) => setConsentForm({ ...consentForm, consent_text: e.target.value })}
                    placeholder="Enter consent text..."
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConsentDialog(false)}>Cancel</Button>
          <Button onClick={handleConsentGrant} variant="contained" disabled={loading}>
            Grant Consent
          </Button>
        </DialogActions>
      </Dialog>

      {/* Privacy Controls Dialog */}
      <Dialog open={showPrivacyDialog} onClose={() => setShowPrivacyDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Privacy Controls</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Data Sharing Settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacyForm.data_sharing_enabled}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, data_sharing_enabled: e.target.checked })}
                  />
                }
                label="Enable data sharing with healthcare providers"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={privacyForm.search_visibility}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, search_visibility: e.target.checked })}
                  />
                }
                label="Allow profile to be found in search"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Profile Visibility
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Profile Visibility</InputLabel>
                <Select
                  value={privacyForm.profile_visibility}
                  onChange={(e) => setPrivacyForm({ ...privacyForm, profile_visibility: e.target.value })}
                  label="Profile Visibility"
                >
                  <MenuItem value="private">Private</MenuItem>
                  <MenuItem value="healthcare_providers">Healthcare Providers Only</MenuItem>
                  <MenuItem value="public">Public</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Data Retention
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Data Retention Period</InputLabel>
                <Select
                  value={privacyForm.data_retention_years}
                  onChange={(e) => setPrivacyForm({ ...privacyForm, data_retention_years: e.target.value })}
                  label="Data Retention Period"
                >
                  <MenuItem value={1}>1 Year</MenuItem>
                  <MenuItem value={3}>3 Years</MenuItem>
                  <MenuItem value={5}>5 Years</MenuItem>
                  <MenuItem value={7}>7 Years</MenuItem>
                  <MenuItem value={10}>10 Years</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacyForm.auto_delete_enabled}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, auto_delete_enabled: e.target.checked })}
                  />
                }
                label="Enable automatic data deletion"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={privacyForm.anonymize_data}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, anonymize_data: e.target.checked })}
                  />
                }
                label="Anonymize data when possible"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPrivacyDialog(false)}>Cancel</Button>
          <Button onClick={handlePrivacyUpdate} variant="contained" disabled={loading}>
            Update Settings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Data Export Dialog */}
      <Dialog open={showExportDialog} onClose={() => setShowExportDialog(false)}>
        <DialogTitle>Export Your Data</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            This will export all your patient data in JSON format. The export will include:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><ProfileIcon /></ListItemIcon>
              <ListItemText primary="Profile Information" />
            </ListItem>
            <ListItem>
              <ListItemIcon><HistoryIcon /></ListItemIcon>
              <ListItemText primary="Medical History" />
            </ListItem>
            <ListItem>
              <ListItemIcon><DocumentIcon /></ListItemIcon>
              <ListItemText primary="Document Metadata" />
            </ListItem>
            <ListItem>
              <ListItemIcon><MessageIcon /></ListItemIcon>
              <ListItemText primary="Communication History" />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExportDialog(false)}>Cancel</Button>
          <Button onClick={handleDataExport} variant="contained" disabled={loading}>
            Export Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Data Deletion Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Your Data</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Warning:</strong> This action cannot be undone. All your patient data will be permanently deleted.
            </Typography>
          </Alert>
          <Typography variant="body1" gutterBottom>
            This will delete:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><ProfileIcon /></ListItemIcon>
              <ListItemText primary="Profile Information" />
            </ListItem>
            <ListItem>
              <ListItemIcon><HistoryIcon /></ListItemIcon>
              <ListItemText primary="Medical History" />
            </ListItem>
            <ListItem>
              <ListItemIcon><DocumentIcon /></ListItemIcon>
              <ListItemText primary="All Documents" />
            </ListItem>
            <ListItem>
              <ListItemIcon><MessageIcon /></ListItemIcon>
              <ListItemText primary="Communication History" />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDataDeletion} variant="contained" color="error" disabled={loading}>
            Delete All Data
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientManagement;
